// src/pages/FarmPulse.js

/**
 * FarmPulse — Market Intelligence Page
 * ------------------------------------------------------------------
 * Mobile-first market intelligence for Rwandan agriculture.
 *
 * - Sticky header: Title + search toggle + PDF icon
 * - Desktop banner with collapsible usage guide
 * - Vertical filter panel (season, district, period, sort) — compact
 * - District insight card when a district is selected
 * - Two tabs: Market + Contribute
 * - Mobile: card grid  |  Desktop: full dashboard table
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Search,
  X,
  FileText,
  WifiOff,
  BookOpen,
  MapPin,
  SlidersHorizontal,
  RotateCcw,
  Check,
} from "lucide-react";
import FarmPulseContribute from "../components/FarmPulseContribute";
import useDebounced from "../hooks/useDebounced";
import FarmPulseMarketTerminal from "../components/FarmPulseMarketTerminal";
import useAuth from "../hooks/useAuth";
import {
  subscribeProducts,
  fetchProductsOnce,
  getCachedProducts,
  getCacheTime,
  extractDistricts,
  filterProducts,
  aggregateByCrop,
  sortCropData,
  getHighlights,
  exportPDF,
} from "../services/farmPulseService";
import {
  WINDOW_OPTIONS,
  SEASONS,
  SORT_OPTIONS,
} from "../constants/farmPulseConstants";

// ─── Hooks ──────────────────────────────────────────────────────

// ─── Relative time helper ───────────────────────────────────────
function relativeTime(ts, t) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  if (diff < 60_000) return t("status.agoJustNow");
  if (diff < 3_600_000) return t("status.agoMinutes", { count: Math.floor(diff / 60_000) });
  if (diff < 86_400_000) return t("status.agoHours", { count: Math.floor(diff / 3_600_000) });
  return t("status.agoOlder");
}

// ─── District Insight Card ──────────────────────────────────────
function DistrictInsight({ district, sortedRows, filtered, t }) {
  if (district === "ALL") return null;

  const avgChange = sortedRows.length > 0
    ? sortedRows.reduce((sum, r) => sum + (r.change30 ?? 0), 0) / sortedRows.length
    : 0;
  const topCrop = sortedRows[0]?.crop ?? "—";
  const reportCount = filtered.length;

  return (
    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-4 h-4 text-green-700 dark:text-green-400" />
        <h3 className="font-bold text-sm text-green-800 dark:text-green-300">
          {t("districtInsight.title", { district })}
        </h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="bg-white dark:bg-neutral-800 rounded-lg px-2.5 py-2">
          <span className="text-gray-500 dark:text-gray-400">{t("districtInsight.crops", { count: sortedRows.length })}</span>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg px-2.5 py-2">
          <span className="text-gray-500 dark:text-gray-400">{t("districtInsight.avgChange")}: </span>
          <span className={avgChange >= 0 ? "text-green-700 dark:text-green-400 font-semibold" : "text-red-600 dark:text-red-400 font-semibold"}>
            {avgChange > 0 ? "+" : ""}{avgChange.toFixed(1)}%
          </span>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg px-2.5 py-2">
          <span className="text-gray-500 dark:text-gray-400">{t("districtInsight.topCrop")}: </span>
          <span className="font-semibold text-gray-900 dark:text-white">{topCrop}</span>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg px-2.5 py-2">
          <span className="text-gray-500 dark:text-gray-400">{t("districtInsight.reports", { count: reportCount })}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Desktop Guide Banner ───────────────────────────────────────
function GuideBanner({ t, open, onClose }) {
  const sectionKeys = [
    "searching", "filtering", "readingPrices",
    "chartTimeframes", "comparing", "kpisForecasts",
    "exporting",
  ];
  const quickSteps = ["step1", "step2", "step3", "step4", "step5", "step6"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="farm-pulse-guide-title">
      <button type="button" className="absolute inset-0" onClick={onClose} aria-label={t("guide.close", { defaultValue: "Close guide" })} />
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-gradient-to-r from-green-800 via-green-700 to-green-600 shadow-xl">
      <div className="absolute inset-0 opacity-[0.04]">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="fp-grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#fp-grid)" />
        </svg>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Top row */}
        <div className="flex items-center justify-between gap-3 py-5">
          <div>
            <h2 id="farm-pulse-guide-title" className="text-lg font-bold text-white">{t("guide.title")}</h2>
            <p className="text-sm text-white/80 mt-1 max-w-lg">{t("subtitle")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={t("guide.close", { defaultValue: "Close guide" })}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Expandable guide */}
        {open && (
          <div className="pb-6 space-y-4 animate-in slide-in-from-top-2 duration-200">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/10">
              <h4 className="font-bold text-sm text-white mb-2">{t("guide.workflowTitle")}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {quickSteps.map((stepKey, idx) => (
                  <div key={stepKey} className="relative rounded-lg bg-white/10 border border-white/15 px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-white/70">{t("guide.step", { count: idx + 1 })}</div>
                    <div className="text-xs text-white font-semibold mt-1 leading-relaxed">{t(`guide.quickSteps.${stepKey}`)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sectionKeys.map((key) => (
              <div key={key} className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/10">
                <h4 className="font-bold text-sm text-white mb-1.5">
                  {t(`guide.sections.${key}.title`)}
                </h4>
                <p className="text-xs text-white/80 leading-relaxed">
                  {t(`guide.sections.${key}.body`)}
                </p>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ─── Tab config ─────────────────────────────────────────────────
function FilterSheet({ open, onClose, onApply, onReset, draft, setDraft, districts, categories, crops, t }) {
  if (!open) return null;
  const fieldClass = "w-full rounded-lg border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600";
  const options = (items, allLabel) => (
    <>
      <option value="ALL">{allLabel}</option>
      {items.map((item) => <option key={item.value || item} value={item.value || item}>{item.label || item}</option>)}
    </>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="farm-pulse-filter-title">
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label={t("filters.close", { defaultValue: "Close filters" })} />
      <div className="relative w-full max-h-[90vh] overflow-y-auto sm:max-w-xl sm:rounded-2xl rounded-t-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
          <h2 id="farm-pulse-filter-title" className="text-base font-semibold text-gray-900 dark:text-white">{t("filters.title", { defaultValue: "Filter market data" })}</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600" aria-label={t("filters.close", { defaultValue: "Close filters" })}><X className="h-5 w-5" aria-hidden="true" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("filters.district")}
            <select className={fieldClass} value={draft.district} onChange={(e) => setDraft((v) => ({ ...v, district: e.target.value }))}>{options(districts, t("filters.allDistricts"))}</select>
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("filters.category")}
            <select className={fieldClass} value={draft.category} onChange={(e) => setDraft((v) => ({ ...v, category: e.target.value }))}>{options(categories, t("filters.allCategories"))}</select>
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("filters.cropType", { defaultValue: "Product type" })}
            <select className={fieldClass} value={draft.crop} onChange={(e) => setDraft((v) => ({ ...v, crop: e.target.value }))}>{options(crops, t("filters.allCrops", { defaultValue: "All products" }))}</select>
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("filters.season")}
            <select className={fieldClass} value={draft.season} onChange={(e) => setDraft((v) => ({ ...v, season: e.target.value }))}>{SEASONS.map((s) => <option key={s.key} value={s.key}>{t(`seasons.${s.key}`)}</option>)}</select>
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("filters.dateRange", { defaultValue: "Date range" })}
            <select className={fieldClass} value={draft.windowDays} onChange={(e) => setDraft((v) => ({ ...v, windowDays: Number(e.target.value) }))}>{WINDOW_OPTIONS.map((d) => <option key={d} value={d}>{t(`windows.${d}`)}</option>)}</select>
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">{t("filters.sortBy")}
            <select className={fieldClass} value={draft.sortBy} onChange={(e) => setDraft((v) => ({ ...v, sortBy: e.target.value }))}>{SORT_OPTIONS.map((s) => <option key={s} value={s}>{t(`sort.${s}`)}</option>)}</select>
          </label>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 dark:border-neutral-800 p-4">
          <button type="button" onClick={onReset} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800"><RotateCcw className="h-4 w-4" aria-hidden="true" />{t("filters.reset", { defaultValue: "Reset" })}</button>
          <button type="button" onClick={onApply} className="ui-btn-primary px-4 py-2 text-sm"><Check className="h-4 w-4" aria-hidden="true" />{t("filters.apply", { defaultValue: "Apply filters" })}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page Component ────────────────────────────────────────
export default function FarmPulse() {
  const { t } = useTranslation("marketAnalysis");
  const { user } = useAuth();
  const location = useLocation();

  const CACHE_TTL_MS = 10 * 60 * 1000;

  // Data
  const [products, setProducts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // UI
  const initialTab = (() => {
    try {
      const params = new URLSearchParams(location.search);
      return params.get("tab") === "contribute" ? "contribute" : "market";
    } catch {
      return "market";
    }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("ALL");
  const [season, setSeason] = useState("ALL");
  const [windowDays, setWindowDays] = useState(90);
  const [sortBy, setSortBy] = useState("gainers");
  const [category, setCategory] = useState("ALL");
  const [crop, setCrop] = useState("ALL");
  const [filterOpen, setFilterOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const debouncedSearch = useDebounced(search, 300);
  const [draftFilters, setDraftFilters] = useState({ district: "ALL", season: "ALL", windowDays: 90, sortBy: "gainers", category: "ALL", crop: "ALL" });

  // Bootstrap cache
  useEffect(() => {
    const cached = getCachedProducts();
    const cacheTime = getCacheTime();
    if (cached.length > 0 && cacheTime && Date.now() - cacheTime < CACHE_TTL_MS) {
      setProducts(cached);
    }
  }, []);

  // Realtime subscription
  useEffect(() => {
    let unsub = null;
    let cancelled = false;

    if (!navigator.onLine) {
      setSyncing(true);
      fetchProductsOnce()
        .then((rows) => { if (!cancelled) { setProducts(rows); setError(null); } })
        .catch(() => { if (!cancelled) setError(t("status.offline")); })
        .finally(() => { if (!cancelled) setSyncing(false); });
      return () => { cancelled = true; };
    }

    setSyncing(true);
    unsub = subscribeProducts(
      (rows) => { setProducts(rows); setSyncing(false); setError(null); },
      () => { setSyncing(false); setError(t("status.offline")); }
    );

    function handleOnline() {
      setTimeout(() => {
        fetchProductsOnce()
          .then((rows) => { if (!cancelled) { setProducts(rows); setError(null); } })
          .catch(() => {});
      }, 600);
    }
    window.addEventListener("online", handleOnline);
    return () => {
      cancelled = true;
      if (unsub) unsub();
      window.removeEventListener("online", handleOnline);
    };
  }, [t]);

  // Derived
  const districts = useMemo(() => extractDistricts(products), [products]);
  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(), [products]);
  const crops = useMemo(() => [...new Set(products.map((p) => p.title || p.name).filter(Boolean))].sort(), [products]);
  const filtered = useMemo(
    () => filterProducts(products, { search: debouncedSearch, district, season, windowDays, category, crop }),
    [products, debouncedSearch, district, season, windowDays, category, crop]
  );
  const byCrop = useMemo(() => aggregateByCrop(filtered, season), [filtered, season]);
  const sortedRows = useMemo(() => sortCropData(byCrop, sortBy), [byCrop, sortBy]);
  const highlights = useMemo(() => getHighlights(byCrop), [byCrop]);

  const cacheTime = getCacheTime();
  const cacheLabel = relativeTime(cacheTime, t);

  const exporterName = user?.displayName || user?.email?.split("@")[0] || "";
  const handlePDF = useCallback(() => exportPDF(sortedRows, highlights, t, exporterName, district), [sortedRows, highlights, t, exporterName, district]);
  const resetFilters = useCallback(() => {
    const reset = { district: "ALL", season: "ALL", windowDays: 90, sortBy: "gainers", category: "ALL", crop: "ALL" };
    setDraftFilters(reset);
    setDistrict(reset.district); setSeason(reset.season); setWindowDays(reset.windowDays); setSortBy(reset.sortBy);
    setCategory(reset.category); setCrop(reset.crop);
  }, []);
  const applyFilters = useCallback(() => {
    setDistrict(draftFilters.district); setSeason(draftFilters.season); setWindowDays(draftFilters.windowDays); setSortBy(draftFilters.sortBy);
    setCategory(draftFilters.category); setCrop(draftFilters.crop);
    setFilterOpen(false);
  }, [draftFilters]);
  const activeFilters = [
    district !== "ALL" && { key: "district", label: district, clear: () => setDistrict("ALL") },
    category !== "ALL" && { key: "category", label: category, clear: () => setCategory("ALL") },
    crop !== "ALL" && { key: "crop", label: crop, clear: () => setCrop("ALL") },
    season !== "ALL" && { key: "season", label: t(`seasons.${season}`), clear: () => setSeason("ALL") },
    windowDays !== 90 && { key: "windowDays", label: t(`windows.${windowDays}`), clear: () => setWindowDays(90) },
    sortBy !== "gainers" && { key: "sortBy", label: t(`sort.${sortBy}`), clear: () => setSortBy("gainers") },
  ].filter(Boolean);

  // ─── Render ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* ═══ Sticky Header ═══ */}
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          {/* Row 1 */}
          <div className="flex items-center justify-between h-12">
            {!searchOpen ? (
              <>
                <div className="flex items-center justify-between gap-1 w-full">
                  <button
                    type="button"
                    onClick={() => { setDraftFilters({ district, season, windowDays, sortBy, category, crop }); setFilterOpen(true); }}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800 sm:h-auto sm:w-auto sm:gap-2 sm:px-3 sm:py-2 sm:text-sm"
                    aria-haspopup="dialog"
                    aria-expanded={filterOpen}
                    aria-label={t("filters.controls", { defaultValue: "Filters and controls" })}
                    title={t("filters.controls", { defaultValue: "Filters and controls" })}
                  >
                    <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
                    <span className="hidden sm:inline">{t("filters.controls", { defaultValue: "Filters & controls" })}</span>
                    {activeFilters.length > 0 ? <span className="rounded-full bg-green-700 px-1.5 text-xs text-white">{activeFilters.length}</span> : null}
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setSearchOpen(true)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300 transition"
                      aria-label={t("filters.search")}
                      title={t("filters.search")}
                    >
                      <Search className="w-5 h-5" aria-hidden="true" />
                    </button>
                    <button
                      onClick={handlePDF}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300 transition"
                      aria-label={t("export.pdf")}
                      title={t("export.pdf")}
                    >
                      <FileText className="w-5 h-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 w-full">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    placeholder={t("filters.search")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-full bg-gray-100 dark:bg-neutral-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 border-none outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <button
                  onClick={() => { setSearchOpen(false); setSearch(""); }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 pb-2.5">
            <button type="button" onClick={() => setActiveTab("contribute")} className="ui-btn-primary justify-center px-4 py-1.5 text-xs">
              {t("tabs.contribute")}
            </button>
          </div>
        </div>
      </div>

      {/* ═══ Main Content ═══ */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
        {activeTab === "market" && (
          <>
            <div className="relative mb-3">
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">{t("table.description")}</p>

              {/* Live status indicator */}
              <div className="mt-1.5 flex items-center gap-1.5 justify-center text-xs sm:mt-0 sm:absolute sm:top-0 sm:right-0">
                {syncing && <span className="orrith-loader orrith-loader-xs orrith-loader-primary" />}
                {error ? (
                  <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <WifiOff className="w-3.5 h-3.5" />
                    {error}
                  </span>
                ) : syncing ? (
                  <span className="text-gray-500">{t("status.syncing")}</span>
                ) : cacheLabel ? (
                  <span className="flex items-center gap-1 text-gray-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    {cacheLabel}
                  </span>
                ) : null}
              </div>
            </div>
            {activeFilters.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" aria-label={t("filters.active", { defaultValue: "Active filters" })}>
                {activeFilters.map((filter) => (
                  <button key={filter.key} type="button" onClick={filter.clear} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-100 dark:border-green-800 dark:bg-green-950/30 dark:text-green-200" aria-label={`${t("filters.remove", { defaultValue: "Remove" })} ${filter.label}`}>
                    {filter.label}<X className="h-3 w-3" aria-hidden="true" />
                  </button>
                ))}
              </div>
            )}

            {/* District insight card */}
            <DistrictInsight
              district={district}
              sortedRows={sortedRows}
              filtered={filtered}
              t={t}
            />

            {/* Loading */}
            {syncing && products.length === 0 && (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="orrith-loader orrith-loader-md orrith-loader-primary" />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{t("status.syncing")}</p>
              </div>
            )}

            {/* Market data */}
            {!(syncing && products.length === 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-4">
                <FarmPulseMarketTerminal rows={sortedRows} windowDays={windowDays} onWindowChange={setWindowDays} />
                <aside className="hidden lg:flex flex-col gap-3">
                  <div className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-sm">
                    <p className="text-caption text-gray-500 dark:text-gray-400">{t("guide.title")}</p>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{t("guide.quickSteps.step1")}</p>
                    <button type="button" onClick={() => setActiveTab("contribute")} className="mt-3 w-full ui-btn-primary px-3 py-2 text-sm">{t("tabs.contribute")}</button>
                  </div>
                  <button type="button" onClick={handlePDF} className="ui-btn-secondary w-full px-3 py-2.5 text-sm"><FileText className="h-4 w-4" aria-hidden="true" />{t("export.pdf")}</button>
                  <button type="button" onClick={() => setGuideOpen(true)} className="ui-btn-secondary w-full px-3 py-2.5 text-sm"><BookOpen className="h-4 w-4" aria-hidden="true" />{t("guide.toggle")}</button>
                </aside>
              </div>
            )}
          </>
        )}

        {activeTab === "contribute" && (
          <div className="max-w-4xl mx-auto pb-24">
            <div className="mb-3 flex items-center justify-between">
              <button type="button" onClick={() => setActiveTab("market")} className="ui-btn-secondary px-3 py-2 text-sm">
                {t("contribute.backToMarket", { defaultValue: "Back to market" })}
              </button>
              <button type="button" onClick={() => setActiveTab("market")} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600" aria-label={t("contribute.close", { defaultValue: "Close contribution" })}>
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Form — takes 3/5 on desktop */}
              <div className="lg:col-span-3">
                <FarmPulseContribute />
              </div>

              {/* Side info panel — desktop only */}
              <div className="hidden lg:block lg:col-span-2 space-y-3">
                <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 p-4">
                  <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-2">
                    {t("contribute.howItWorks", { defaultValue: "How it works" })}
                  </h3>
                  <ul className="space-y-2.5 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    <li className="flex gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center justify-center text-[10px] font-bold">1</span>
                      <span>{t("contribute.step1", { defaultValue: "Submit crop prices from your local market. Every submission is saved immediately." })}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center justify-center text-[10px] font-bold">2</span>
                      <span>{t("contribute.step2", { defaultValue: "When 5 independent contributors report similar prices for the same crop within 7 days, the price is validated." })}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center justify-center text-[10px] font-bold">3</span>
                      <span>{t("contribute.step3", { defaultValue: "Validated prices power the Market tab — trends, forecasts, and alerts that help all farmers." })}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center justify-center text-[10px] font-bold">4</span>
                      <span>{t("contribute.step4", { defaultValue: "Earn 10 Impact Points when your price report becomes validated. One price per crop per day keeps data trustworthy." })}</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-100 dark:border-green-900/30 p-4">
                  <h3 className="font-bold text-sm text-green-800 dark:text-green-300 mb-1.5">
                    {t("contribute.whyContribute", { defaultValue: "Why contribute?" })}
                  </h3>
                  <p className="text-xs text-green-700 dark:text-green-400 leading-relaxed">
                    {t("contribute.whyBody", { defaultValue: "Market intelligence helps farmers sell at the right time, buyers find fair prices, and policymakers track food security. Your data makes a real difference." })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <FilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
        draft={draftFilters}
        setDraft={setDraftFilters}
        districts={districts}
        categories={categories}
        crops={crops}
        t={t}
      />
      {guideOpen ? <GuideBanner t={t} open={guideOpen} onClose={() => setGuideOpen(false)} /> : null}
    </div>
  );
}
